/**
 * ResolutionSelector - Selector for image resolution.
 *
 * The per-option description renders in a small hover-delayed popover
 * anchored to the info icon next to the label — matches AspectRatioSelector
 * and the Media Library `tooltip.css` pattern.
 */
import { RESOLUTION_OPTIONS } from '@/types/creativeStudio';
import type { ImageResolution } from '@/types/creativeStudio';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import '@/styles/tooltip.css';

interface ResolutionSelectorProps {
  value: ImageResolution;
  onChange: (resolution: ImageResolution) => void;
  disabled?: boolean;
}

export function ResolutionSelector({ value, onChange, disabled }: ResolutionSelectorProps) {
  const selectedOption = RESOLUTION_OPTIONS.find((opt) => opt.value === value);
  const description = selectedOption?.description;
  const tooltipId = description ? 'resolution-hint-tooltip' : undefined;

  return (
    <div>
      <div className="flex items-center gap-1 mb-2">
        <label htmlFor="resolution-select" className="block text-sm font-medium text-gray-700">
          Resolution
        </label>
        {description && (
          <div className="tooltip-container">
            <button
              type="button"
              aria-label={`About the ${selectedOption?.label} resolution`}
              aria-describedby={tooltipId}
              className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
            >
              <InformationCircleIcon className="h-4 w-4 text-gray-400" />
            </button>
            <span
              id={tooltipId}
              role="tooltip"
              className="tooltip-text tooltip-text-left"
              style={{ whiteSpace: 'normal', width: '18rem', maxWidth: '60vw', textAlign: 'left' }}
            >
              {description}
            </span>
          </div>
        )}
      </div>
      <select
        id="resolution-select"
        value={value}
        onChange={(e) => onChange(e.target.value as ImageResolution)}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm
                   focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                   disabled:bg-gray-100 disabled:text-gray-500"
      >
        {RESOLUTION_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
